import React, { useEffect, useState, useCallback } from 'react';
import { Plus, MapPin, Ban, CheckCircle2, Unlock, Pencil, Trash2 } from 'lucide-react';
import api from '../../api/client';
import CalendarioMensileStaff from './CalendarioMensileStaff';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';

const GIORNI_OPTS = [{ v: 1, l: 'L' }, { v: 2, l: 'M' }, { v: 3, l: 'M' }, { v: 4, l: 'G' }, { v: 5, l: 'V' }, { v: 6, l: 'S' }, { v: 0, l: 'D' }];
const fmtData = (d) => new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });

export default function Campi() {
  const { user } = useAuth();
  const perm = {
    create: puoFare(user, 'campi', 'create'),
    edit:   puoFare(user, 'campi', 'edit'),
    delete: puoFare(user, 'campi', 'delete'),
  };
  const [tab, setTab] = useState('cal'); // cal | prenotazioni | gestione
  const [msg, setMsg] = useState('');
  const toast = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>AFFITTO CAMPI</h1>
          <p style={s.sub}>Sedi, campi e slot per l'affitto ai corsisti — calendario e gestione prenotazioni.</p>
        </div>
      </div>

      {msg && <div style={s.toast}>{msg}</div>}

      <div style={s.tabBar}>
        <button style={{ ...s.tab, ...(tab === 'cal' ? s.tabActive : {}) }} onClick={() => setTab('cal')}>CALENDARIO</button>
        <button style={{ ...s.tab, ...(tab === 'prenotazioni' ? s.tabActive : {}) }} onClick={() => setTab('prenotazioni')}>PRENOTAZIONI</button>
        <button style={{ ...s.tab, ...(tab === 'gestione' ? s.tabActive : {}) }} onClick={() => setTab('gestione')}>GESTIONE SEDI E SLOT</button>
      </div>

      {tab === 'cal' && <TabCalendario toast={toast} />}
      {tab === 'prenotazioni' && <TabPrenotazioni toast={toast} perm={perm} />}
      {tab === 'gestione' && <TabGestione toast={toast} perm={perm} />}
    </div>
  );
}

// ─────────────────────────────────────────
// TAB CALENDARIO — vista mensile con gli slot e i nomi già visibili;
// clicca un giorno per aprire sotto il dettaglio e intervenire (prenota/segna occupato/pagato/annulla)
// ─────────────────────────────────────────

function TabCalendario() {
  return <CalendarioMensileStaff />;
}

// ─────────────────────────────────────────
// TAB PRENOTAZIONI — elenco completo, filtri, pagato, annulla, sblocca
// ─────────────────────────────────────────

function TabPrenotazioni({ toast, perm }) {
  const [sedi, setSedi] = useState([]);
  const [righe, setRighe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ sede_id: '', campo_id: '', stato: '', pagato: '', da: '', a: '' });

  useEffect(() => { api.get('/campi/sedi').then(r => setSedi(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    try { const r = await api.get('/campi/prenotazioni', { params }); setRighe(r.data); }
    catch { setRighe([]); }
    finally { setLoading(false); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const campiDisponibili = f.sede_id ? (sedi.find(s2 => String(s2.id) === String(f.sede_id))?.campi || []) : [];

  const togglePagato = async (r) => {
    try { await api.patch(`/campi/${r.id}/pagato`, { pagato: !r.pagato }); load(); }
    catch (e) { toast('✗ ' + (e.response?.data?.error || 'Errore')); }
  };
  const annulla = async (r) => {
    if (!window.confirm(`Annullare la prenotazione ${r.codice}?`)) return;
    try { await api.post(`/campi/${r.id}/annulla`); load(); toast('Prenotazione annullata'); }
    catch (e) { toast('✗ ' + (e.response?.data?.error || 'Errore')); }
  };
  const sblocca = async (r) => {
    if (!window.confirm('Sbloccare questo slot?')) return;
    try { await api.delete(`/campi/blocco/${r.id}`); load(); toast('Slot sbloccato'); }
    catch (e) { toast('✗ ' + (e.response?.data?.error || 'Errore')); }
  };

  return (
    <div>
      <div style={s.filterBar}>
        <select style={s.dateInput} value={f.sede_id} onChange={e => setF({ ...f, sede_id: e.target.value, campo_id: '' })}>
          <option value="">Tutte le sedi</option>
          {sedi.map(s2 => <option key={s2.id} value={s2.id}>{s2.nome}</option>)}
        </select>
        <select style={s.dateInput} value={f.campo_id} onChange={e => setF({ ...f, campo_id: e.target.value })} disabled={!f.sede_id}>
          <option value="">Tutti i campi</option>
          {campiDisponibili.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
        <select style={s.dateInput} value={f.stato} onChange={e => setF({ ...f, stato: e.target.value })}>
          <option value="">Tutti gli stati</option>
          <option value="CONFERMATA">Confermata</option>
          <option value="ANNULLATA">Annullata</option>
          <option value="BLOCCATA">Bloccata</option>
        </select>
        <select style={s.dateInput} value={f.pagato} onChange={e => setF({ ...f, pagato: e.target.value })}>
          <option value="">Pagato: tutti</option>
          <option value="true">Pagato</option>
          <option value="false">Non pagato</option>
        </select>
        <label style={s.label}>DAL</label>
        <input type="date" style={s.dateInput} value={f.da} onChange={e => setF({ ...f, da: e.target.value })} />
        <label style={s.label}>AL</label>
        <input type="date" style={s.dateInput} value={f.a} onChange={e => setF({ ...f, a: e.target.value })} />
      </div>

      {loading && <div style={{ color: '#666', padding: '20px' }}>Caricamento...</div>}
      {!loading && righe.length === 0 && <div style={{ color: '#666', padding: '20px' }}>Nessuna prenotazione trovata.</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {righe.map(r => (
          <div key={r.id} style={s.card}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)' }}>
                {r.campo?.sede?.nome || r.sede_nome} — {r.campo?.nome || r.campo_nome} · {fmtData(r.data)} {r.ora_inizio}–{r.ora_fine}
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: '#888', marginTop: '2px' }}>
                {r.stato === 'BLOCCATA' ? (r.note || 'Bloccato dallo staff') : `${r.nome} · ${r.email || ''} ${r.telefono ? '· ' + r.telefono : ''}`}
                {' · '}{r.codice}{r.modalita ? ` · ${r.modalita.nome}` : ''}
              </div>
            </div>
            <div style={{ minWidth: '70px', textAlign: 'right', fontWeight: 700 }}>€ {r.prezzo.toFixed(2)}</div>
            <span style={{ fontSize: '10px', fontWeight: 800, letterSpacing: '1px', padding: '4px 10px', borderRadius: '20px', color: r.stato === 'CONFERMATA' ? '#22C55E' : r.stato === 'BLOCCATA' ? '#EF4444' : '#888', background: r.stato === 'CONFERMATA' ? '#22C55E20' : r.stato === 'BLOCCATA' ? '#EF444420' : '#88888820' }}>{r.stato}</span>
            {r.stato === 'CONFERMATA' && (
              <>
                {perm.edit && <button style={{ ...s.iconBtn, color: r.pagato ? '#22C55E' : '#888' }} onClick={() => togglePagato(r)} title={r.pagato ? 'Segna come non pagato' : 'Segna come pagato'}>
                  <CheckCircle2 size={14} /> {r.pagato ? 'Pagato' : 'Non pagato'}
                </button>}
                <button style={{ ...s.iconBtn, color: '#EF4444' }} onClick={() => annulla(r)}><Ban size={13} /></button>
              </>
            )}
            {r.stato === 'BLOCCATA' && perm.delete && (
              <button style={{ ...s.iconBtn, color: '#22C55E' }} onClick={() => sblocca(r)}><Unlock size={13} /> Sblocca</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// TAB GESTIONE — sedi, campi, slot, modalità di pagamento
// ─────────────────────────────────────────

function TabGestione({ toast, perm }) {
  const [sedi, setSedi] = useState([]);
  const [modalita, setModalita] = useState([]);
  const [showNewSede, setShowNewSede] = useState(false);
  const [nuovaSede, setNuovaSede] = useState({ nome: '', indirizzo: '' });
  const [nuovaModalita, setNuovaModalita] = useState('');

  const load = useCallback(async () => {
    const [s1, m1] = await Promise.all([api.get('/campi/sedi'), api.get('/campi/modalita-pagamento')]);
    setSedi(s1.data); setModalita(m1.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const creaSede = async (e) => {
    e.preventDefault();
    if (!nuovaSede.nome.trim()) return;
    try { await api.post('/campi/sedi', nuovaSede); setNuovaSede({ nome: '', indirizzo: '' }); setShowNewSede(false); load(); toast('Sede creata'); }
    catch (err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); }
  };

  const toggleSedeAttiva = async (sede) => {
    await api.put(`/campi/sedi/${sede.id}`, { attiva: !sede.attiva });
    load();
  };

  const eliminaSede = async (sede) => {
    const nPren = (sede.campi || []).reduce((tot, c) => tot + (c._count?.prenotazioni || 0), 0);
    const msg = nPren > 0
      ? `Questa sede ha ${nPren} prenotazioni nello storico. Verranno mantenute con i dati già registrati, ma scollegate dalla sede. Eliminare comunque "${sede.nome}" e tutti i suoi campi/slot?`
      : `Eliminare definitivamente la sede "${sede.nome}" e tutti i suoi campi/slot?`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/campi/sedi/${sede.id}`); load(); toast('Sede eliminata'); }
    catch (err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); }
  };

  const creaModalita = async (e) => {
    e.preventDefault();
    if (!nuovaModalita.trim()) return;
    try { await api.post('/campi/modalita-pagamento', { nome: nuovaModalita.trim() }); setNuovaModalita(''); load(); toast('Modalità creata — aprila per aggiungere le istruzioni'); }
    catch (err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); }
  };
  const toggleModalita = async (m) => { await api.put(`/campi/modalita-pagamento/${m.id}`, { attiva: !m.attiva }); load(); };
  const salvaModalita = async (id, data) => {
    try { await api.put(`/campi/modalita-pagamento/${id}`, data); load(); toast('Modalità aggiornata'); }
    catch (err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={s.formTitle}>SEDI</div>
        {perm.create && <button style={s.btnPri} onClick={() => setShowNewSede(v => !v)}><Plus size={14} /> NUOVA SEDE</button>}
      </div>

      {showNewSede && (
        <form onSubmit={creaSede} style={{ ...s.formCard, display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '16px' }}>
          <div style={{ flex: 1 }}><label style={s.label}>NOME SEDE</label><input style={s.input} autoFocus value={nuovaSede.nome} onChange={e => setNuovaSede(v => ({ ...v, nome: e.target.value }))} placeholder="es. Robbiate" /></div>
          <div style={{ flex: 1 }}><label style={s.label}>INDIRIZZO</label><input style={s.input} value={nuovaSede.indirizzo} onChange={e => setNuovaSede(v => ({ ...v, indirizzo: e.target.value }))} /></div>
          <button type="submit" style={s.btnPri}>CREA</button>
        </form>
      )}

      {sedi.length === 0 && <div style={{ color: '#666', padding: '16px 0' }}>Nessuna sede creata. Creane una per iniziare.</div>}

      {sedi.map(sede => <SedeCard key={sede.id} sede={sede} toggleAttiva={toggleSedeAttiva} eliminaSede={eliminaSede} toast={toast} reload={load} perm={perm} />)}

      <div style={{ ...s.formTitle, marginTop: '28px' }}>MODALITÀ DI PAGAMENTO</div>
      <p style={{ color: '#666', fontSize: 'var(--font-size-xs)', marginBottom: '10px' }}>Chi prenota indica quale userà, e vede le istruzioni che scrivi qui (IBAN per un bonifico, numero per Satispay, ecc.) dopo la conferma e nella email.</p>
      {perm.create && <form onSubmit={creaModalita} style={{ display: 'flex', gap: '10px', marginBottom: '12px', maxWidth: '360px' }}>
        <input style={s.input} placeholder="es. Satispay" value={nuovaModalita} onChange={e => setNuovaModalita(e.target.value)} />
        <button type="submit" style={s.btnSec}><Plus size={13} /></button>
      </form>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {modalita.map(m => <ModalitaCard key={m.id} modalita={m} onToggle={() => toggleModalita(m)} onSalva={(data) => salvaModalita(m.id, data)} perm={perm} />)}
      </div>
    </div>
  );
}

function SedeCard({ sede, toggleAttiva, eliminaSede, toast, reload, perm }) {
  const [aperta, setAperta] = useState(false);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({ nome: sede.nome, indirizzo: sede.indirizzo || '', messaggio_conferma: sede.messaggio_conferma || '' });
  const [showNewCampo, setShowNewCampo] = useState(false);
  const [nomeCampo, setNomeCampo] = useState('');

  const creaCampo = async (e) => {
    e.preventDefault();
    if (!nomeCampo.trim()) return;
    try { await api.post(`/campi/sedi/${sede.id}/campi`, { nome: nomeCampo.trim() }); setNomeCampo(''); setShowNewCampo(false); reload(); toast('Campo creato'); }
    catch (err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); }
  };

  const salvaSede = async (e) => {
    e.preventDefault();
    try { await api.put(`/campi/sedi/${sede.id}`, form); setEditando(false); reload(); toast('Sede aggiornata'); }
    catch (err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); }
  };

  return (
    <div style={{ ...s.card, flexDirection: 'column', alignItems: 'stretch', padding: '14px 18px', opacity: sede.attiva ? 1 : 0.55 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }} onClick={() => setAperta(v => !v)}>
        <MapPin size={15} color="#F4600C" />
        <div style={{ flex: 1, fontWeight: 700 }}>{sede.nome} {sede.indirizzo && <span style={{ color: '#666', fontWeight: 400, fontSize: 'var(--font-size-xs)' }}>— {sede.indirizzo}</span>}</div>
        <span style={{ fontSize: 'var(--font-size-xs)', color: '#666' }}>{sede.campi.length} campi</span>
        {perm.edit && <button style={s.iconBtn} onClick={(e) => { e.stopPropagation(); setEditando(v => !v); setAperta(true); }}><Pencil size={12} /></button>}
        {perm.edit && <button style={s.iconBtn} onClick={(e) => { e.stopPropagation(); toggleAttiva(sede); }}>{sede.attiva ? 'Disattiva' : 'Riattiva'}</button>}
        {perm.delete && <button style={{ ...s.iconBtn, color: '#EF4444' }} onClick={(e) => { e.stopPropagation(); eliminaSede(sede); }}><Trash2 size={12} /></button>}
      </div>

      {aperta && editando && (
        <form onSubmit={salvaSede} style={{ ...s.formCard, marginTop: '12px' }}>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <div style={{ flex: 1 }}><label style={s.label}>NOME SEDE</label><input style={s.input} value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} /></div>
            <div style={{ flex: 1 }}><label style={s.label}>INDIRIZZO</label><input style={s.input} value={form.indirizzo} onChange={e => setForm(f => ({ ...f, indirizzo: e.target.value }))} /></div>
          </div>
          <label style={s.label}>MESSAGGIO PER IL CLIENTE (opzionale)</label>
          <textarea style={{ ...s.input, height: '60px', resize: 'vertical', marginBottom: '10px' }} placeholder='es. "Il pallone non è incluso nella prenotazione."' value={form.messaggio_conferma} onChange={e => setForm(f => ({ ...f, messaggio_conferma: e.target.value }))} />
          <p style={{ fontSize: '10px', color: '#666', marginBottom: '10px' }}>Mostrato al cliente dopo la conferma e nella email di conferma, per questa sede.</p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="button" style={s.btnSec} onClick={() => setEditando(false)}>ANNULLA</button>
            <button type="submit" style={s.btnPri}>SALVA</button>
          </div>
        </form>
      )}

      {aperta && !editando && (
        <div style={{ marginTop: '14px', paddingLeft: '24px', borderLeft: '2px solid #222' }}>
          {sede.campi.map(campo => <CampoBlock key={campo.id} campo={campo} toast={toast} reload={reload} perm={perm} />)}
          {perm.create && (showNewCampo ? (
            <form onSubmit={creaCampo} style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <input style={s.input} autoFocus placeholder="es. Campo 1" value={nomeCampo} onChange={e => setNomeCampo(e.target.value)} />
              <button type="submit" style={s.btnSec}>OK</button>
            </form>
          ) : (
            <button style={{ ...s.btnSec, marginTop: '8px' }} onClick={() => setShowNewCampo(true)}><Plus size={13} /> AGGIUNGI CAMPO</button>
          ))}
        </div>
      )}
    </div>
  );
}

function ModalitaCard({ modalita, onToggle, onSalva, perm }) {
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({ nome: modalita.nome, istruzioni: modalita.istruzioni || '' });

  const salva = (e) => {
    e.preventDefault();
    onSalva(form);
    setEditando(false);
  };

  return (
    <div style={{ ...s.card, flexDirection: 'column', alignItems: 'stretch', padding: '12px 16px', opacity: modalita.attiva ? 1 : 0.5 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ flex: 1, fontWeight: 700 }}>{modalita.nome} {!modalita.attiva && <span style={{ color: '#666', fontWeight: 400, fontSize: 'var(--font-size-xs)' }}>(disattivata)</span>}</div>
        {perm.edit && <button style={s.iconBtn} onClick={() => setEditando(v => !v)}><Pencil size={12} /></button>}
        {perm.edit && <button style={s.iconBtn} onClick={onToggle}>{modalita.attiva ? 'Disattiva' : 'Riattiva'}</button>}
      </div>
      {!editando && modalita.istruzioni && <div style={{ fontSize: 'var(--font-size-xs)', color: '#888', marginTop: '6px', whiteSpace: 'pre-wrap' }}>{modalita.istruzioni}</div>}
      {!editando && !modalita.istruzioni && <div style={{ fontSize: 'var(--font-size-xs)', color: '#555', marginTop: '6px' }}>Nessuna istruzione impostata — il cliente non vedrà dettagli per questa modalità.</div>}
      {editando && (
        <form onSubmit={salva} style={{ marginTop: '10px' }}>
          <label style={s.label}>NOME</label>
          <input style={{ ...s.input, marginBottom: '8px' }} value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} />
          <label style={s.label}>ISTRUZIONI PER IL CLIENTE (IBAN, numero, ecc.)</label>
          <textarea style={{ ...s.input, height: '80px', resize: 'vertical', marginBottom: '10px' }} placeholder={'es.\nIBAN: IT00...\nIntestatario: ...\nBanca: ...'} value={form.istruzioni} onChange={e => setForm(f => ({ ...f, istruzioni: e.target.value }))} />
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="button" style={s.btnSec} onClick={() => setEditando(false)}>ANNULLA</button>
            <button type="submit" style={s.btnPri}>SALVA</button>
          </div>
        </form>
      )}
    </div>
  );
}

const SLOT_VUOTO = { ora_inizio: '18:30', durata_minuti: 90, prezzo: 40, giorni_settimana: [1, 2, 3, 4, 5], valido_da: '', valido_a: '' };

function CampoBlock({ campo, toast, reload, perm }) {
  const [showNewSlot, setShowNewSlot] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const toggleCampoAttivo = async () => { await api.put(`/campi/campi/${campo.id}`, { attivo: !campo.attivo }); reload(); };
  const toggleSlotAttivo = async (slot) => { await api.put(`/campi/slot/${slot.id}`, { attivo: !slot.attivo }); reload(); };

  const eliminaCampo = async () => {
    const n = campo._count?.prenotazioni || 0;
    const msg = n > 0
      ? `Questo campo ha ${n} prenotazioni nello storico. Verranno mantenute con i dati già registrati, ma scollegate dal campo. Eliminare comunque "${campo.nome}" e tutti i suoi slot?`
      : `Eliminare definitivamente il campo "${campo.nome}" e tutti i suoi slot?`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/campi/campi/${campo.id}`); reload(); toast('Campo eliminato'); }
    catch (err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); }
  };

  const eliminaSlot = async (slot) => {
    const n = slot._count?.prenotazioni || 0;
    const msg = n > 0
      ? `Questo slot ha ${n} prenotazioni nello storico. Verranno mantenute con i dati già registrati, ma scollegate dallo slot. Eliminarlo comunque?`
      : `Eliminare definitivamente questo slot (${slot.ora_inizio})?`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/campi/slot/${slot.id}`); reload(); toast('Slot eliminato'); }
    catch (err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); }
  };

  const creaSlot = async (form) => {
    try { await api.post(`/campi/campi/${campo.id}/slot`, form); setShowNewSlot(false); reload(); toast('Slot creato'); }
    catch (err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); }
  };
  const salvaSlot = async (slotId, form) => {
    try { await api.put(`/campi/slot/${slotId}`, form); setEditingId(null); reload(); toast('Slot aggiornato'); }
    catch (err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); }
  };

  return (
    <div style={{ marginBottom: '14px', opacity: campo.attivo ? 1 : 0.5 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
        <div style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)' }}>{campo.nome}</div>
        {perm.edit && <button style={s.iconBtn} onClick={toggleCampoAttivo}>{campo.attivo ? 'Disattiva' : 'Riattiva'}</button>}
        {perm.delete && <button style={{ ...s.iconBtn, color: '#EF4444' }} onClick={eliminaCampo}><Trash2 size={12} /></button>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {campo.slot.map(slot => (
          <div key={slot.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: 'var(--font-size-xs)', color: slot.attivo ? '#ccc' : '#666', background: 'var(--bg-input)', borderRadius: '6px', padding: '6px 10px', cursor: perm.edit ? 'pointer' : 'default' }}
              onClick={() => perm.edit && setEditingId(editingId === slot.id ? null : slot.id)}>
              <span style={{ fontWeight: 700, minWidth: '48px' }}>{slot.ora_inizio}</span>
              <span>{slot.durata_minuti} min</span>
              <span>€ {slot.prezzo.toFixed(2)}</span>
              <span>{GIORNI_OPTS.filter(g => slot.giorni_settimana.includes(g.v)).map(g => g.l).join(' ')}</span>
              {(slot.valido_da || slot.valido_a) && (
                <span style={{ color: '#F4600C' }}>
                  {slot.valido_da ? fmtData(slot.valido_da) : '…'} → {slot.valido_a ? fmtData(slot.valido_a) : '…'}
                </span>
              )}
              <span style={{ marginLeft: 'auto' }}>{slot.attivo ? 'attivo' : 'disattivato'}</span>
              {perm.edit && <Pencil size={11} />}
              {perm.edit && <button style={s.iconBtn} onClick={(e) => { e.stopPropagation(); toggleSlotAttivo(slot); }}>{slot.attivo ? <Ban size={12} /> : <CheckCircle2 size={12} />}</button>}
              {perm.delete && <button style={{ ...s.iconBtn, color: '#EF4444' }} onClick={(e) => { e.stopPropagation(); eliminaSlot(slot); }}><Trash2 size={12} /></button>}
            </div>
            {editingId === slot.id && (
              <SlotForm
                iniziale={{ ora_inizio: slot.ora_inizio, durata_minuti: slot.durata_minuti, prezzo: slot.prezzo, giorni_settimana: slot.giorni_settimana, valido_da: slot.valido_da ? slot.valido_da.slice(0, 10) : '', valido_a: slot.valido_a ? slot.valido_a.slice(0, 10) : '' }}
                onAnnulla={() => setEditingId(null)}
                onSalva={(form) => salvaSlot(slot.id, form)}
                submitLabel="SALVA MODIFICHE"
              />
            )}
          </div>
        ))}
        {campo.slot.length === 0 && <div style={{ color: '#555', fontSize: 'var(--font-size-xs)' }}>Nessuno slot.</div>}
      </div>

      {perm.create && (showNewSlot ? (
        <SlotForm iniziale={SLOT_VUOTO} onAnnulla={() => setShowNewSlot(false)} onSalva={creaSlot} submitLabel="CREA SLOT" />
      ) : (
        <button style={{ ...s.btnSec, marginTop: '6px' }} onClick={() => setShowNewSlot(true)}><Plus size={12} /> AGGIUNGI SLOT</button>
      ))}
    </div>
  );
}

function SlotForm({ iniziale, onAnnulla, onSalva, submitLabel }) {
  const [form, setForm] = useState(iniziale);
  const toggleGiorno = (v) => setForm(f => ({ ...f, giorni_settimana: f.giorni_settimana.includes(v) ? f.giorni_settimana.filter(g => g !== v) : [...f.giorni_settimana, v] }));

  const submit = (e) => {
    e.preventDefault();
    onSalva({ ...form, valido_da: form.valido_da || null, valido_a: form.valido_a || null });
  };

  return (
    <form onSubmit={submit} style={{ ...s.formCard, marginTop: '8px', padding: '12px' }}>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <div><label style={s.label}>ORARIO</label><input type="time" style={s.input} value={form.ora_inizio} onChange={e => setForm(f => ({ ...f, ora_inizio: e.target.value }))} /></div>
        <div><label style={s.label}>DURATA (min)</label><input type="number" style={{ ...s.input, width: '80px' }} value={form.durata_minuti} onChange={e => setForm(f => ({ ...f, durata_minuti: +e.target.value }))} /></div>
        <div><label style={s.label}>PREZZO €</label><input type="number" step="0.01" style={{ ...s.input, width: '90px' }} value={form.prezzo} onChange={e => setForm(f => ({ ...f, prezzo: +e.target.value }))} /></div>
      </div>
      <label style={s.label}>GIORNI</label>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '10px' }}>
        {GIORNI_OPTS.map(g => (
          <button type="button" key={g.v} onClick={() => toggleGiorno(g.v)} style={{ width: '30px', height: '30px', borderRadius: '6px', border: '1px solid var(--border2)', background: form.giorni_settimana.includes(g.v) ? '#F4600C' : 'var(--bg-input)', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>{g.l}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <div><label style={s.label}>VALIDO DAL (opzionale)</label><input type="date" style={s.input} value={form.valido_da} onChange={e => setForm(f => ({ ...f, valido_da: e.target.value }))} /></div>
        <div><label style={s.label}>AL (opzionale)</label><input type="date" style={s.input} value={form.valido_a} onChange={e => setForm(f => ({ ...f, valido_a: e.target.value }))} /></div>
      </div>
      <p style={{ fontSize: '10px', color: '#666', marginBottom: '10px' }}>Lasciando vuoto uno o entrambi i campi, lo slot resta valido senza limite in quella direzione. Usali per spalmare lo slot su un arco definito (es. solo l'estate).</p>
      <div style={{ display: 'flex', gap: '10px' }}>
        <button type="button" style={s.btnSec} onClick={onAnnulla}>ANNULLA</button>
        <button type="submit" style={s.btnPri}>{submitLabel}</button>
      </div>
    </form>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'var(--font-size-title)', fontWeight: '800', letterSpacing: '2px' },
  sub: { color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)', marginTop: '4px', maxWidth: '560px' },
  tabBar: { display: 'flex', gap: '4px', marginBottom: '18px', borderBottom: '1px solid var(--border)' },
  tab: { padding: '9px 16px', background: 'transparent', border: 'none', borderBottom: '2px solid transparent', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 700, letterSpacing: '1px', color: '#666' },
  tabActive: { color: '#F4600C', borderBottomColor: '#F4600C' },
  toast: { background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '10px 16px', marginBottom: '16px', fontSize: 'var(--font-size-sm)' },
  btnPri: { display: 'flex', alignItems: 'center', gap: '8px', background: '#F4600C', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 14px', fontSize: 'var(--font-size-xs)', fontWeight: '700', cursor: 'pointer' },
  btnSec: { display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--border)', color: 'var(--text)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '8px 12px', fontSize: 'var(--font-size-xs)', fontWeight: '700', cursor: 'pointer' },
  filterBar: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' },
  dateInput: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '6px', padding: '6px 10px', color: 'var(--text)', fontSize: 'var(--font-size-xs)', outline: 'none' },
  label: { fontSize: 'var(--font-size-xs)', letterSpacing: '1px', fontWeight: '700', color: 'var(--text-dim)', display: 'block', marginBottom: '4px' },
  formCard: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' },
  formTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'var(--font-size-sm)', fontWeight: '700', letterSpacing: '2px', color: 'var(--text-dim)' },
  input: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '9px 12px', color: 'var(--text)', fontSize: 'var(--font-size-sm)', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' },
  card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '14px' },
  iconBtn: { display: 'flex', alignItems: 'center', gap: '5px', background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '6px', padding: '6px 10px', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 700 },
  pill: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '20px', padding: '6px 14px', color: 'var(--text)', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer' },
};
